import React, { useEffect, useState } from "react";
import {
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  FormControlLabel,
  Switch,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  container: {
    width: "100%",
  },
  card: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderRadius: 8,
  },
  cardTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  field: {
    width: "100%",
  },
  actions: {
    marginTop: theme.spacing(2),
    textAlign: "right",
  },
}));

const CHANNELS = [
  { key: "whatsapp", label: "WhatsApp" },
  { key: "facebook", label: "Facebook" },
  { key: "instagram", label: "Instagram" },
  { key: "tiktok", label: "TikTok" },
];

const DEFAULT_POLICY = {
  firstResponseMinutes: 60,
  nextResponseMinutes: 120,
  resolutionMinutes: 1440,
  isActive: true,
};

const SlaPolicies = () => {
  const classes = useStyles();
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchPolicies = async () => {
      try {
        const { data } = await api.get("/sla/policies");
        const list = (data?.policies || []).map((p) => ({
          ...DEFAULT_POLICY,
          ...p,
        }));
        setPolicies(list);
      } catch (err) {
        toast.error(i18n.t("slaPolicies.toasts.loadError"));
      }
    };
    fetchPolicies();
  }, []);

  const handleChange = (channel, field, value) => {
    setPolicies((prev) =>
      prev.map((p) =>
        p.channel === channel ? { ...p, [field]: value } : p
      )
    );
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await api.post("/sla/policies", { policies });
      toast.success(i18n.t("slaPolicies.toasts.saved"));
    } catch (err) {
      toast.error(i18n.t("slaPolicies.toasts.saveError"));
    } finally {
      setLoading(false);
    }
  };

  const ensureChannels = () => {
    if (policies.length === 0) {
      return CHANNELS.map((c) => ({ channel: c.key, ...DEFAULT_POLICY }));
    }
    const byChannel = policies.reduce((acc, p) => {
      acc[p.channel] = p;
      return acc;
    }, {});
    return CHANNELS.map((c) => ({
      channel: c.key,
      ...DEFAULT_POLICY,
      ...(byChannel[c.key] || {}),
    }));
  };

  const items = ensureChannels();

  return (
    <div className={classes.container}>
      <Typography variant="h6" gutterBottom>
        {i18n.t("slaPolicies.title")}
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        {i18n.t("slaPolicies.description")}
      </Typography>

      {items.map((policy) => {
        const channelLabel =
          CHANNELS.find((c) => c.key === policy.channel)?.label ||
          policy.channel;

        return (
          <Paper key={policy.channel} className={classes.card} elevation={1}>
            <Typography className={classes.cardTitle}>
              {channelLabel}
            </Typography>
            <Grid spacing={2} container>
              <Grid xs={12} md={4} item>
                <TextField
                  className={classes.field}
                  label={i18n.t("slaPolicies.fields.firstResponseMinutes")}
                  type="number"
                  variant="outlined"
                  value={policy.firstResponseMinutes}
                  onChange={(e) =>
                    handleChange(
                      policy.channel,
                      "firstResponseMinutes",
                      Number(e.target.value)
                    )
                  }
                />
              </Grid>
              <Grid xs={12} md={4} item>
                <TextField
                  className={classes.field}
                  label={i18n.t("slaPolicies.fields.nextResponseMinutes")}
                  type="number"
                  variant="outlined"
                  value={policy.nextResponseMinutes}
                  onChange={(e) =>
                    handleChange(
                      policy.channel,
                      "nextResponseMinutes",
                      Number(e.target.value)
                    )
                  }
                />
              </Grid>
              <Grid xs={12} md={4} item>
                <TextField
                  className={classes.field}
                  label={i18n.t("slaPolicies.fields.resolutionMinutes")}
                  type="number"
                  variant="outlined"
                  value={policy.resolutionMinutes}
                  onChange={(e) =>
                    handleChange(
                      policy.channel,
                      "resolutionMinutes",
                      Number(e.target.value)
                    )
                  }
                />
              </Grid>
              <Grid xs={12} item>
                <FormControlLabel
                  control={
                    <Switch
                      checked={policy.isActive}
                      onChange={(e) =>
                        handleChange(
                          policy.channel,
                          "isActive",
                          e.target.checked
                        )
                      }
                      color="primary"
                    />
                  }
                  label={i18n.t("common.active")}
                />
              </Grid>
            </Grid>
          </Paper>
        );
      })}

      <div className={classes.actions}>
        <Button
          color="primary"
          variant="contained"
          onClick={handleSave}
          disabled={loading}
        >
          {i18n.t("slaPolicies.buttons.save")}
        </Button>
      </div>
    </div>
  );
};

export default SlaPolicies;
